import { format, isValid } from "date-fns";
import type { KeyboardEvent } from "react";

import type { TimelineEvent } from "@/@types/access-request.types";
import type {
  IUnlockDetails,
  IUnlockEligibleRecord,
  ListStateInput,
  UnlockListState,
  UnlockStoreScope,
} from "@/@types/unlock-request";
import { SITUATION_STATUS } from "@/constants/access-request";
import { EmployeeSituation } from "@/constants/employee-situation";
import { UNLOCK_DETAIL_LABELS } from "@/constants/messages";
import { DEFAULT_PER_PAGE } from "@/constants/pagination";
import {
  UNLOCK_DATE_FORMAT,
  UNLOCK_EMPTY_CELL,
  UNLOCK_ENTER_KEY,
  UNLOCK_LIST_STATE,
  UNLOCK_PER_PAGE_OPTIONS,
  UNLOCK_STORE_SCOPE,
  UNLOCK_TIMELINE_TYPE,
} from "@/constants/unlock-request";
import { EnumTypeAccessRequest } from "@/enum/access_request";
import type { HistoryDTO } from "@/services/dtos/accessRequest.dto";
import { unlockEligibleRecordKey } from "@/services/unlock-eligible-pages";
import { applyCpfMaskIfNeeded } from "@/utils/cpf-mask";
import { readInt } from "@/utils/search-params";

export const unlockRecordKey = unlockEligibleRecordKey;

export const unlockRecordName = (record: IUnlockEligibleRecord): string =>
  record.operatorName ?? record.userName ?? "-";

export const isUnlockReleasable = (record: IUnlockEligibleRecord): boolean =>
  record.situationStatus === SITUATION_STATUS.FOUND &&
  record.situation === EmployeeSituation.WORKING;

export const resolveStoreScope = (
  isLoadingStores: boolean,
  isStoresError: boolean,
  total: number,
): UnlockStoreScope => {
  if (isLoadingStores) return UNLOCK_STORE_SCOPE.LOADING;
  if (isStoresError) return UNLOCK_STORE_SCOPE.ERROR;
  if (total === 0) return UNLOCK_STORE_SCOPE.NONE;
  if (total === 1) return UNLOCK_STORE_SCOPE.SINGLE;

  return UNLOCK_STORE_SCOPE.MANY;
};

export const resolveListState = ({
  storeScope,
  hasStore,
  isLoadingRecords,
  isRecordsError,
  total,
}: ListStateInput): UnlockListState => {
  if (storeScope === UNLOCK_STORE_SCOPE.ERROR) {
    return UNLOCK_LIST_STATE.STORES_FAILED;
  }
  if (storeScope === UNLOCK_STORE_SCOPE.NONE) return UNLOCK_LIST_STATE.NO_STORE;
  if (storeScope === UNLOCK_STORE_SCOPE.LOADING) return UNLOCK_LIST_STATE.LOADING;
  if (!hasStore) return UNLOCK_LIST_STATE.PICK_STORE;
  if (isRecordsError) return UNLOCK_LIST_STATE.FAILED;
  if (isLoadingRecords) return UNLOCK_LIST_STATE.LOADING;
  if (total === 0) return UNLOCK_LIST_STATE.EMPTY;

  return UNLOCK_LIST_STATE.ROWS;
};

export const readUnlockPerPage = (raw: string | null): number => {
  const requested: number = readInt(raw, DEFAULT_PER_PAGE);

  return UNLOCK_PER_PAGE_OPTIONS.includes(requested)
    ? requested
    : DEFAULT_PER_PAGE;
};

export const keepSearchFromSubmitting = (
  event: KeyboardEvent<HTMLInputElement>,
): void => {
  if (event.key !== UNLOCK_ENTER_KEY) return;

  event.preventDefault();
};

export const toTimelineEvent = (entry: HistoryDTO): TimelineEvent => ({
  id: entry.id,
  created_at: entry.created_at ?? "",
  type: UNLOCK_TIMELINE_TYPE[entry.type] ?? EnumTypeAccessRequest.UPDATED,
  comment: entry.comment,
  responsible: entry.responsible,
});

export const readWasBlocked = (unlock: IUnlockDetails | null): string => {
  if (typeof unlock?.wasBlocked !== "boolean") return UNLOCK_DETAIL_LABELS.UNKNOWN;

  return unlock.wasBlocked ? UNLOCK_DETAIL_LABELS.YES : UNLOCK_DETAIL_LABELS.NO;
};

export const formatUnlockCpf = (cpf: string | null): string =>
  cpf ? applyCpfMaskIfNeeded(cpf) : UNLOCK_EMPTY_CELL;

export const formatUnlockDate = (raw: string | null): string => {
  if (!raw) return UNLOCK_EMPTY_CELL;

  const date: Date = new Date(raw);
  return isValid(date) ? format(date, UNLOCK_DATE_FORMAT) : UNLOCK_EMPTY_CELL;
};
